import { memo } from 'react';
import {
  ChevronDown,
  Columns2,
  GitCompareArrows,
  Link2,
  List,
  MoveUpRight,
  Radius,
} from 'lucide-react';
import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuTrigger,
  IconButton,
  Tooltip,
} from '@/components/ui';
import {
  EXAGGERATION_OPTIONS,
  exaggerationLabel,
  type Exaggeration,
} from './mapDisplay';

const GROUP =
  'inline-flex items-center gap-0.5 rounded-lg border border-border bg-surface p-0.5 shadow-sm';

export interface MapToolbarProps {
  residuals: {
    shown: boolean;
    onToggle: () => void;
    exaggeration: Exaggeration;
    /** Exaggeration applies to residual vectors and uncertainty circles. */
    exaggerationEnabled: boolean;
    onExaggerationChange: (value: Exaggeration) => void;
  } | null;
  uncertainty: {
    shown: boolean;
    onToggle: () => void;
    /** Why circles are unavailable for this fit, if they are. */
    unavailableReason?: string;
  } | null;
  compare: { label: string; pressed: boolean; onToggle: () => void } | null;
  link: { pressed: boolean; onToggle: () => void } | null;
  sideBySide: { pressed: boolean; onToggle: () => void };
  legend: { open: boolean; onToggle: () => void; controls: string };
}

/** Compact icon toolbar: map layers, then view and linking tools. */
export const MapToolbar = memo(function MapToolbar({
  residuals,
  uncertainty,
  compare,
  link,
  sideBySide,
  legend,
}: MapToolbarProps) {
  const hasLayers = residuals || uncertainty || compare;
  return (
    <div
      role="group"
      aria-label="Map tools"
      className="flex flex-wrap items-center gap-2"
    >
      {hasLayers && (
        <div className={GROUP}>
          {residuals && (
            <>
              <IconButton
                aria-label="Residual vectors"
                size="sm"
                aria-pressed={residuals.shown}
                onClick={residuals.onToggle}
                icon={<MoveUpRight />}
              />
              <DropdownMenu>
                <Tooltip content="Residual exaggeration">
                  <DropdownMenuTrigger
                    asChild
                    disabled={!residuals.exaggerationEnabled}
                  >
                    <Button
                      variant="ghost"
                      size="sm"
                      aria-label={`Residual exaggeration: ${exaggerationLabel(residuals.exaggeration)}`}
                      className="gap-0.5 px-1.5 font-mono text-xs"
                      trailingIcon={<ChevronDown className="!size-3" />}
                    >
                      {exaggerationLabel(residuals.exaggeration)}
                    </Button>
                  </DropdownMenuTrigger>
                </Tooltip>
                <DropdownMenuContent align="start" className="min-w-[11rem]">
                  <DropdownMenuLabel>Residual exaggeration</DropdownMenuLabel>
                  <DropdownMenuRadioGroup
                    value={String(residuals.exaggeration)}
                    onValueChange={(value) =>
                      residuals.onExaggerationChange(
                        value === 'auto' ? 'auto' : Number(value)
                      )
                    }
                  >
                    {EXAGGERATION_OPTIONS.map((option) => (
                      <DropdownMenuRadioItem
                        key={String(option.value)}
                        value={String(option.value)}
                      >
                        {option.label}
                      </DropdownMenuRadioItem>
                    ))}
                  </DropdownMenuRadioGroup>
                </DropdownMenuContent>
              </DropdownMenu>
            </>
          )}
          {uncertainty && (
            <IconButton
              aria-label="95% uncertainty circles"
              size="sm"
              aria-pressed={uncertainty.shown}
              onClick={uncertainty.onToggle}
              disabled={!!uncertainty.unavailableReason}
              disabledReason={uncertainty.unavailableReason}
              icon={<Radius />}
            />
          )}
          {compare && (
            <IconButton
              aria-label={compare.label}
              size="sm"
              aria-pressed={compare.pressed}
              onClick={compare.onToggle}
              icon={<GitCompareArrows />}
            />
          )}
        </div>
      )}
      <div className={GROUP}>
        {link && (
          <IconButton
            aria-label="Link points"
            size="sm"
            aria-pressed={link.pressed}
            onClick={link.onToggle}
            icon={<Link2 />}
          />
        )}
        <IconButton
          aria-label="Side-by-side"
          tooltip="Side-by-side Record and Field"
          size="sm"
          aria-pressed={sideBySide.pressed}
          onClick={sideBySide.onToggle}
          icon={<Columns2 />}
        />
        <IconButton
          aria-label="Legend"
          size="sm"
          aria-expanded={legend.open}
          aria-controls={legend.controls}
          onClick={legend.onToggle}
          className={legend.open ? 'bg-accent-muted text-accent' : undefined}
          icon={<List />}
        />
      </div>
    </div>
  );
});
